import * as ko from 'knockout';

import { ListRequestParams } from '../api/request';
import { BaseLoadingScreen } from './base_loading_screen';
import { ListLoaderDelegate, delegateActions } from '../components/list_loader';
import { Region } from '../models/region';
import * as regionsApi from '../api/regions';
import { countriesApi } from '../api/countries';
import { ListHeaderAction } from '../components/list_header';
import i18n from '../i18n';
import { translate } from '../i18n_text';
import { session } from '../session';
import { canCreateRegion, canDeleteRegion, canEditRegion, reactListTablesEnabled } from '../permissions';
import { GridView } from '../utils/grid_view';
import { GridChoices, listLookup } from '../utils/grid_choices';
import {
  RegionsListChoices,
  RegionsListPage,
  RegionsListProps,
  RegionsListQuery,
  RegionsListRow,
} from 'react-islands';

let template = require('raw-loader!../../templates/regions.html').default;

class RegionsScreen extends BaseLoadingScreen implements ListLoaderDelegate<regionsApi.RegionData, Region> {
  canDelete = canDeleteRegion();
  listActions = ko.observableArray<ListHeaderAction>([]);

  newRegionsTable = reactListTablesEnabled();

  private view = new GridView(
    'administrative-regions',
    new GridChoices<RegionsListChoices>(
      {
        country: listLookup(countriesApi.list, 'country'),
      },
      () => ({
        country: [],
      })
    )
  );

  tableProps = ko.pureComputed<RegionsListProps>(() => ({
    view: this.view,
    fetchPage: this.fetchPage,
    exportFileName: 'administrative-regions',
  }));

  constructor(params: { filters: unknown }) {
    super();

    const canCreate = canCreateRegion();
    if (canCreate) {
      this.listActions.push({
        title: i18n.t('Add')(),
        icon: 'add_circle',
        href: '/regions/new/',
        tooltipTitle: i18n.t('Add Administrative Region')(),
      });
    }
    // Import bulk-creates and updates records, so it needs both add and change.
    if (canCreate && canEditRegion()) {
      this.listActions.push({
        title: i18n.t('Import')(),
        icon: 'file_upload',
        href: '/regions/import/',
      });
    }

    this.loadedAfter(this.newRegionsTable ? this.view.load(params.filters) : Promise.resolve());
  }

  fetch(params: ListRequestParams) {
    return regionsApi.list(params);
  }

  private fetchPage = (query: RegionsListQuery): Promise<RegionsListPage> =>
    this.view.fetchListPage(query, regionsApi.list, 'name_prefix', this);

  toRow = (region: Region): RegionsListRow => ({
    id: region.id(),
    name: translate(region.nameJson()),
    url: session.toTenantPath(region.editUrl()),
    countryName: region.country()?.name || '',
    actions: delegateActions(this, region, this.view.reload),
  });

  instantiate = (regionData: regionsApi.RegionData) => new Region(regionData);

  remove(id: string) {
    return regionsApi.remove(id);
  }

  canRemove(region: Region) {
    return this.canDelete;
  }
}

export let regions = {
  name: 'regions',
  viewModel: RegionsScreen,
  template: template,
};

ko.components.register(regions.name, regions);
